import 'whatwg-fetch';

import {SkipToContent} from '@wandb/components';
import {ThemeProvider} from '@wandb/ui';
import {useIsFirstRender} from '@wandb/weave/common/util/hooks';
import {clsx} from 'clsx';
import * as queryString from 'query-string';
import * as React from 'react';
import {useContext, useEffect, useMemo, useRef} from 'react';
import {Redirect, useLocation} from 'react-router-dom';

import ErrorPage from './components/ErrorPage';
import ErrorPortal from './components/ErrorPortal';
import NoMatch from './components/NoMatch';
import {RefreshAlert} from './components/RefreshAlert/RefreshAlert';
import {AccountSelectorContext} from './components/Search/SearchNav/AccountSelectorContextProvider';
import config, {envIsIntegration, envIsLocal, envIsProd} from './config';
import {LoaderProxy} from './containers/AppLoader';
import {captureError, shouldReloadOnError} from './integrations';
import {LOGOUT_PATH} from './routes/auth/authPaths';
import {ONBOARDING_FLOW_PATH} from './routes/onboarding/onboardingPaths';
import {TrackRouteComponent} from './routes/TrackRouteComponent';
import {isInJupyterNotebook} from './setup';
import {displayError} from './state/global/actions';
import {useDispatch, useSelector} from './state/hooks';
import {
  selectAppViewerState,
  selectViewerEmail,
} from './state/viewer/selectors';
import {type UserInfo} from './types/graphql';
import {viewerUsingAdminPrivileges} from './util/admin';
import {hideZendeskChat, showZendeskChat} from './util/analytics';
import {disableBeamer, openBeamerOnLoad} from './util/beamer';
import {PUBLISHED_PROJECT_NAME} from './util/constants';
import {getDatadogRum} from './util/datadog/datadogRum';
import {startProfilerPageView} from './util/datadog/profiler';
import {useMeticulousRecorder} from './util/hooks/useMeticulousRecorder';
import {safeLocalStorage} from './util/localStorage';
import {mouseListenersStart} from './util/mouse';
import {useRampFlagParallelGzip} from './util/rampFeatureFlags/rampFeatureFlags';
import startTrace from './util/trace';
import {isInIframe} from './util/window';

const HOW_OFTEN_QUESTION_DATE = new Date('2021-10-20T08:00:00Z');
const WHICH_TOOLS_QUESTION_DATE = new Date('2022-01-29T08:00:00Z');
const ML_USE_CASES_QUESTION_DATE = new Date('2022-02-12T08:00:00Z');

// Error Boundary wrapper - React doesn't support error boundaries in functional components yet
class AppErrorBoundary extends React.Component<
  {children: React.ReactNode; dispatch: any},
  {}
> {
  componentDidCatch(error: any, info: any) {
    // Send to Sentry
    captureError(error, 'app_componentdidcatch');
    // Send to Datadog RUM
    const dd_rum = getDatadogRum();
    dd_rum.addError(error, {
      message: 'app_componentdidcatch',
    });
    this.props.dispatch(displayError(error));
    if (!shouldReloadOnError()) {
      console.log({error});
    }
  }

  render() {
    return this.props.children;
  }
}

export const App = ({children}: {children: React.ReactNode}) => {
  // DO NOT REMOVE THIS useOnLocationChange()
  // We need it to re-render App on every location change,
  // which is necessary due to how routes are being rendered.
  useOnLocationChange();

  const location = useLocation();
  const dispatch = useDispatch();

  // Note: Very strange... if the selector passed in to the next line
  // (to grab state.global.error) is a globally defined function instead
  // of an inline function, we always get undefined back, at least after
  // faking a login error. This could be a serious issue but I haven't
  // been able to figure out the cause. Defining it inline at least
  // gives the correct result.
  const error = useSelector(state => state.global.error);
  const showErrorPortal = useSelector(state => state.global.showErrorPortal);
  const errorPortalContent = useSelector(
    state => state.global.errorPortalContent
  );

  // TODO: we should verify the unstable profile images don't cause the app
  // to re-render.  See state/viewer/hooks.ts#useViewer
  const viewerState = useSelector(selectAppViewerState);

  // Cache the parallel gzip flag for synchronous access in the Apollo Link chain
  useRampFlagParallelGzip();

  const needsOnboardingRedirect = useNeedsOnboardingRedirect(viewerState);

  const isEmailVerificationError =
    error != null &&
    error.code === 403 &&
    typeof error.message === 'string' &&
    /Email must be verified/.test(error.message);

  // disable beamer on the email verification page
  // We disable Beamer auto-open for first time signups for a day
  useEffect(() => {
    if (needsOnboardingRedirect || isEmailVerificationError) {
      disableBeamer();
    } else if (viewerState.hasViewer) {
      openBeamerOnLoad();
    }
  }, [
    needsOnboardingRedirect,
    isEmailVerificationError,
    viewerState.hasViewer,
  ]);

  const appClassNames = useMemo(
    () =>
      clsx('app-root', {
        iframe: isInIframe(),
        jupyter: isInJupyterNotebook(),
      }),
    []
  );

  useEffect(() => {
    mouseListenersStart();

    if (isInIframe()) {
      hideZendeskChat();
    }

    return () => {
      if (isInIframe()) {
        showZendeskChat();
      }
    };
  }, []);

  /**
   * WARNING: Returning the loader here instead of the app should be done with extreme caution. The reason to block the app until a query returns is that the query data is _required_ for a minimally functional app. This is rarely the case.
   *
   * Because data fetching in a React app is tied to component lifecycles, blocking subcomponents from rendering with loading screens will defer the initiation of those data requests, and create a cascade of serially data calls. E.g.
   * 1a. the app mounts and requests the viewer object: request takes 300ms
   * 1b. the app mounts and requests the gallery tags: request takes 500ms
   * 2. the project page component mounts and requests project data: request 400ms
   * 3. the workspace pane then renders and fetches its data: 350ms
   * Total app loading time _after_ the DOM is mounted is an additional (500+400+350)ms. This is because the slowest resolving query at each level will block _all_ subsequent queries from firing. Paralleization helps, but the minimium response to undo each block will always be the slowest query in the batch.
   *
   * Most of the time the proper place is to block rendering UI at the lowest possible level, not the highest.
   * ```
   * <App>
   *   <ProjectPage>
   *     <MultiRunsWorkSpace>
   *       <Loader> || <MultiRunsWorkspaceData />
   *     </MultiRunsWorkSpace>
   *   </ProjectPage>
   * </App>
   * ```
   * This ensures that the rest of the UI not dependent on the async data can render as fast as possible.
   *
   * Note: The viewer and the galleryTags are legacy, and an ongoing performance initiative is hoping to remove one or both (if possible) from being a critical rendering block here.
   */
  if (viewerState.loading) {
    return <LoaderProxy name="viewer-state-loader" />;
  }

  // Wait until the viewer is done reloading after being mutated
  // before we redirect back to /signup, so that we're actually sure
  // the user still needs to sign up. This avoids redirect loops from
  // the stale viewer object.
  if (needsOnboardingRedirect) {
    return (
      <Redirect
        to={{pathname: ONBOARDING_FLOW_PATH, state: {internal: true}}}
      />
    );
  }

  const usingAdminPowers = viewerState.admin && viewerUsingAdminPrivileges();

  const onPublishedEditPage = location.pathname.includes(
    PUBLISHED_PROJECT_NAME
  );
  const onHiddenPage = !usingAdminPowers && onPublishedEditPage;

  return (
    <AppErrorBoundary dispatch={dispatch}>
      <ThemeProvider>
        <SkipToContent />
        <div className={appClassNames}>
          {error ? (
            <TrackRouteComponent routeName="ErrorPage">
              <ErrorPage error={error} />
            </TrackRouteComponent>
          ) : onHiddenPage ? (
            <TrackRouteComponent routeName="NoMatch">
              <NoMatch />
            </TrackRouteComponent>
          ) : (
            children
          )}
          <ErrorPortal open={showErrorPortal}>{errorPortalContent}</ErrorPortal>
          <RefreshAlert />
        </div>
      </ThemeProvider>
    </AppErrorBoundary>
  );
};

export const MeticulousWrapper = ({children}: {children: React.ReactNode}) => {
  const account = useContext(AccountSelectorContext);

  useMeticulousRecorder(account.selectedAccount?.name);
  return <>{children}</>;
};

function useOnLocationChange(): void {
  const viewerEmail = useSelector(selectViewerEmail);

  const isInitialPageview = useIsFirstRender();
  const lastHREFRef = useRef<string>(window.location.href);
  useEffect(() => {
    if (!isInitialPageview) {
      trackHostPageView({viewerEmail, referrer: lastHREFRef.current});
    }
    lastHREFRef.current = window.location.href;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [window.location.href]);

  useEffect(() => {
    startTrace();
    startProfilerPageView();
    // disabling this because we want this to be triggered by href, but
    // the lint rule complains about the unnecessary dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [window.location.href]);
}

type TrackHostPageViewParams = {
  viewerEmail: string | undefined;
  referrer: string;
};

function trackHostPageView({
  viewerEmail,
  referrer,
}: TrackHostPageViewParams): void {
  // Ignore tracking server instances using the host function because they
  // have higher requirements for data privacy
  if (
    config.ENVIRONMENT_IS_PRIVATE ||
    !(envIsProd || config.ENVIRONMENT_NAME === 'qa')
  ) {
    return;
  }

  const qs = queryString.parse(window.location.search);
  const body = {
    appPageParams: {
      properties: {
        user_id: viewerEmail,
        url: window.location.href,
        path: window.location.pathname,
        search: window.location.search,
        referrer,
        title: document.title,
      },
      context: {
        userAgent: window.navigator.userAgent,
        locale: window.navigator.language,
        campaign: {
          name: qs.utm_campaign,
          source: qs.utm_source,
          medium: qs.utm_medium,
          term: qs.utm_term,
          content: qs.utm_content,
        },
      },
    },
  };

  // eslint-disable-next-line wandb/no-unprefixed-urls
  fetch('/__WB_PAGEVIEW__', {
    method: 'POST',
    headers: {'Content-Type': 'application/json'},
    body: JSON.stringify(body),
  });
}

type UserInfoCheck = {
  accessor: keyof UserInfo;
  liveDate: Date;
};

function useNeedsOnboardingRedirect(
  viewerState: ReturnType<typeof selectAppViewerState>
): boolean {
  const {pathname} = useLocation();

  const needsToCompleteSurvey = useMemo(() => {
    const hasIncompleteSurvey = viewerHasIncompleteSurvey(
      viewerState.createdAt,
      viewerState.userInfo
    );
    return hasIncompleteSurvey && !config.ENVIRONMENT_IS_PRIVATE;
  }, [viewerState.createdAt, viewerState.userInfo]);

  useEffect(() => {
    if (needsToCompleteSurvey) {
      safeLocalStorage.setItem('survey_incomplete', 'true');
    }
  }, [needsToCompleteSurvey]);

  return useMemo(() => {
    // Enforce user to choose a username
    const isAtSignup = pathname.endsWith(ONBOARDING_FLOW_PATH);
    const isAtLogout = pathname.endsWith(LOGOUT_PATH);
    const isUsernameRequired =
      viewerState.hasViewer && !(isAtLogout || isAtSignup);

    const isOnboardingIncompleteAndNotOnOnboardingPage =
      viewerState.userInfo?.isOnboardingIncomplete === true &&
      !isAtSignup &&
      !isAtLogout;

    return (
      !viewerState.reload &&
      ((isUsernameRequired && viewerState.signupRequired) ||
        isOnboardingIncompleteAndNotOnOnboardingPage)
    );
  }, [
    pathname,
    viewerState.hasViewer,
    viewerState.reload,
    viewerState.signupRequired,
    viewerState.userInfo,
  ]);
}

function viewerHasIncompleteSurvey(
  viewerCreatedAt: string | undefined,
  viewerUserInfo: UserInfo | undefined
) {
  // test integration initiates users without survey
  // so always return false to skip this checking and not block tests
  if (envIsIntegration || envIsLocal) {
    return false;
  }

  if (viewerCreatedAt == null) {
    return false;
  }
  const viewerCreatedAtDate = new Date(viewerCreatedAt);

  const userInfoChecks: UserInfoCheck[] = [
    {accessor: `howOften`, liveDate: HOW_OFTEN_QUESTION_DATE},
    {accessor: `whichTools`, liveDate: WHICH_TOOLS_QUESTION_DATE},
    {accessor: `mlUseCases`, liveDate: ML_USE_CASES_QUESTION_DATE},
  ];
  return userInfoChecks
    .filter(c => c.liveDate <= viewerCreatedAtDate)
    .some(c => viewerUserInfo?.[c.accessor] == null);
}
